import React from 'react';
import { Navigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import MuiCard from '@mui/material/Card';
import CssBaseline from '@mui/material/CssBaseline';
import { styled } from '@mui/material/styles';
import AppTheme from './shared-theme/AppTheme';
import { RefreshTokenManager } from './utils/refreshTokenManager';
import { startOctopusLoadSse } from './utils/loadOctopusSse';

export function getExpiryDate(jwt: string): Date {
  const decoded = atob(jwt.split(".")[1]);
  const json = JSON.parse(decoded);
  const exp = json.exp;
  return new Date(exp * 1000);
}

const RefreshingCard = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  ...theme.applyStyles('dark', {
    background: 'hsla(220, 30%, 3%, 1)',
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

function RefreshingSessionView() {
  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            backgroundImage: theme.palette.mode === 'light'
              ? 'url(/light-bg.jpg)'
              : 'url(/dark-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            overflow: 'auto',
          })}
        >
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', backgroundColor: 'transparent' }}>
              <Box
                sx={(theme) => ({
                  width: '100%',
                  maxWidth: '1200px',
                  backgroundColor: theme.palette.surface,
                  minHeight: 'calc(100vh - 64px)',
                  px: { xs: 0, md: 3 },
                  pb: 1,
                })}
              />
            </Box>
          </Stack>
        </Box>
      </Box>
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.text.primary,
          zIndex: theme.zIndex.drawer + 1,
        })}
        open
      >
        <RefreshingCard variant="outlined" sx={{ mx: { xs: '20px', md: 4 } }}>
          <Stack
            direction="column"
            sx={{ justifyContent: 'center', alignItems: 'center', gap: 4, maxWidth: 450 }}
          >
            <CircularProgress color="inherit" size={80} />
            <Typography sx={{ textAlign: 'center' }}>Loading. Please wait...</Typography>
          </Stack>
        </RefreshingCard>
      </Backdrop>
    </AppTheme>
  );
}

export default function withAuth<T extends object>(Component: React.ComponentType<T>) {
  const AuthRoute = (props: T) => {
    const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
    const [isRefreshing, setIsRefreshing] = React.useState(false);

    React.useEffect(() => {
      const checkAuth = async () => {
        const token = localStorage.getItem("token");
        
        if (!token) {
          // No token at all, check if we have a refresh token
          if (RefreshTokenManager.hasRefreshToken()) {
            setIsRefreshing(true);
            const refreshed = await RefreshTokenManager.refreshToken();
            setIsRefreshing(false);
            setIsAuthenticated(refreshed);
            if (refreshed) {
              startOctopusLoadSse(localStorage.getItem('user_id') ?? '');
            }
          } else {
            setIsAuthenticated(false);
          }
          return;
        }

        // We have a token, check if it's expired
        if (RefreshTokenManager.isTokenExpired(5)) {
          // Token is expired or will expire soon, try to refresh
          if (RefreshTokenManager.hasRefreshToken()) {
            setIsRefreshing(true);
            const refreshed = await RefreshTokenManager.refreshToken();
            setIsRefreshing(false);
            setIsAuthenticated(refreshed);
            if (refreshed) {
              startOctopusLoadSse(localStorage.getItem('user_id') ?? '');
            }
          } else {
            // No refresh token, clear expired token and redirect to login
            RefreshTokenManager.clearTokens();
            setIsAuthenticated(false);
          }
        } else {
          // Token is still valid
          setIsAuthenticated(true);
        }
      };

      checkAuth();
    }, []);

    // Show dashboard shell + refreshing popup while checking/refreshing authentication
    if (isAuthenticated === null || isRefreshing) {
      return <RefreshingSessionView />;
    }

    if (isAuthenticated) {
      return <Component {...props} />;
    } else {
      return <Navigate to="/" />;
    }
  };

  return AuthRoute;
}
